import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, MenuItem, TextField,
} from '@mui/material';

import { exportLevels, exportPyramid } from '../Viewer/exportRender';
import { fileSink, memorySink } from '../Viewer/tiffWriter';

const MEMORY_LIMIT = 300e6;  // sans écriture directe sur le disque : au plus autant de pixels (fichier en mémoire)
const fmt = n => n.toLocaleString('en-US');
const duration = s => (s >= 3600 ? `${Math.floor(s / 3600)} h ${Math.round((s % 3600) / 60)} min`
  : s >= 60 ? `${Math.floor(s / 60)} min ${Math.round(s % 60)} s` : `${Math.round(s)} s`);

// Téléchargement en pleine résolution de ce qui est affiché (getSnapshot : { layers, background } au moment de
// l'export), sur la zone gardée (region), en OME-TIFF pyramidal. name : nom de la modalité ; fileName : nom proposé.
export default function ExportDialog({ open, onClose, getSnapshot, region, name, fileName, pixelSizeUm }) {
  const [quality, setQuality] = useState(0.9);
  const [state, setState] = useState(null);  // { done, total, started } pendant l'export ; { result } ou { error } après
  const abort = useRef(null);
  useEffect(() => { if (open) setState(null); }, [open]);
  if (!region) return null;
  const width = Math.round(region.x1 - region.x0);
  const height = Math.round(region.y1 - region.y0);
  const levels = exportLevels(width, height);
  const tiles = levels.reduce((a, l) => a + l.across * l.down, 0);
  const direct = typeof window.showSaveFilePicker === 'function';
  const tooBig = !direct && width * height > MEMORY_LIMIT;
  const running = state && 'done' in state;

  const start = async () => {
    let sink;
    try {
      sink = direct ? await fileSink(fileName) : memorySink();
    } catch (err) {
      if (err.name !== 'AbortError') setState({ error: err.message });  // choix du fichier annulé : rien
      return;
    }
    const controller = new AbortController();
    abort.current = controller;
    const started = Date.now();
    setState({ done: 0, total: tiles, started });
    try {
      const { layers, background } = getSnapshot();
      const size = await exportPyramid({
        layers, background, region, sink, quality, name, pixelSizeUm, signal: controller.signal,
        onProgress: ({ done, total }) => setState({ done, total, started }),
      });
      if (!direct) {
        const url = URL.createObjectURL(sink.blob());
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
      setState({ result: `Saved: ${fileName} (${(size / 1e6).toLocaleString('en-US', { maximumFractionDigits: 1 })} MB) in ${duration((Date.now() - started) / 1000)}.` });
    } catch (err) {
      setState(err.name === 'AbortError' ? { error: 'Export stopped.' } : { error: `Export failed: ${err.message}` });
    } finally {
      abort.current = null;
    }
  };

  const progress = running ? state.done / state.total : 0;
  const elapsed = running ? (Date.now() - state.started) / 1000 : 0;
  return (
    <Dialog open={open} onClose={running ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Download at full resolution</DialogTitle>
      <DialogContent>
        <p style={{ marginTop: 0 }}>
          <strong>{name}</strong>, with the displayed settings (LUT, contrast, computed channels, erased areas){' '}
          on {region.cropped ? 'the cropped rectangle' : 'the whole image'}: {fmt(width)} × {fmt(height)} px.
        </p>
        <p>
          Pyramidal OME-TIFF (BigTIFF, {levels.length} levels, {fmt(tiles)} 512 px RGB JPEG tiles
          {pixelSizeUm ? `, ${pixelSizeUm.toLocaleString('en-US')} µm pixels` : ''}): opens in QuPath and Fiji, and can be
          uploaded back to HyprView. The legends and the scale bar are not included.
        </p>
        <TextField select size="small" label="JPEG quality" value={quality} disabled={running}
          onChange={e => setQuality(Number(e.target.value))} sx={{ minWidth: 200 }}>
          <MenuItem value={0.8}>80% (lighter)</MenuItem>
          <MenuItem value={0.9}>90%</MenuItem>
          <MenuItem value={0.95}>95% (more faithful)</MenuItem>
        </TextField>
        {!direct && (
          <Alert severity={tooBig ? 'error' : 'info'} sx={{ mt: 2 }}>
            {tooBig
              ? 'This browser cannot write such a large file directly to disk: use Chrome or Edge, or crop a smaller area.'
              : 'File prepared in memory, then downloaded (Chrome and Edge write directly to disk).'}
          </Alert>
        )}
        {running && (
          <div style={{ marginTop: 16 }}>
            <LinearProgress variant="determinate" value={progress * 100} />
            <p style={{ marginBottom: 0 }}>
              {fmt(state.done)} / {fmt(state.total)} tiles
              {progress > 0.02 ? ` · about ${duration((elapsed / progress) * (1 - progress))} left` : ''}
              {' '}· faster if this tab stays in the foreground.
            </p>
          </div>
        )}
        {state?.result && <Alert severity="success" sx={{ mt: 2 }}>{state.result}</Alert>}
        {state?.error && <Alert severity="warning" sx={{ mt: 2 }}>{state.error}</Alert>}
      </DialogContent>
      <DialogActions>
        {running
          ? <Button onClick={() => abort.current?.abort()}>Stop</Button>
          : <Button onClick={onClose}>Close</Button>}
        {!running && (
          <Button variant="contained" onClick={start} disabled={tooBig}>
            {direct ? 'Save…' : 'Download'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
